import { CircleHelp, ShieldAlert } from "lucide-react";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { resolveIdentityDisplayName } from "../../constants/identityTiers.constants";
import { IdentitiesService } from "../../services/identitiesService";
import type { Identity } from "../../types/identities-api.types";
import type { CreateIdentityResponseDto } from "../../types/identity-form.types";
import { resolveNewHolderOutcome, type NewHolderOutcome } from "../../utils/newHolderOutcome";
import { PersonHolderForm } from "../holder/PersonHolderForm";

/** Distinct from `CreateHolderModal`'s id: both can be in the DOM at once, and `form=` matches by id. */
const FORM_ID = "create-card-new-holder-form";

/** What the drawer shows after a creation it could not turn into a selectable holder. */
type OutcomeState = Exclude<NewHolderOutcome, { kind: "eligible" }>;

export interface NewHolderDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fires once per creation, after the read-back. On `eligible` the drawer closes itself, so the host only
   * has to select the holder; on `pending` it stays open to explain why nothing was selected.
   */
  onResolved: (outcome: NewHolderOutcome) => void;
  /** Opens the holder's detail page. The card draft goes with it — it could not be issued anyway. */
  onViewHolder: (identityId: string) => void;
}

/**
 * Creating a card holder without leaving the new-card modal.
 *
 * **A side panel, not a second modal.** A centred dialog of the same size would land exactly on top of the
 * card draft; the panel takes the right band instead, so on a wide screen the alias and the pocket stay
 * visible behind it. It is `lg` because the form is 16 fields in two columns — at `md` those columns get
 * about 200px each and the country and date pickers turn cramped. The trade is deliberate: below roughly
 * 1400px the panel does cover most of the modal, and under `sm` all of it. What the operator never loses is
 * the draft itself, which lives in the host's state and is untouched either way.
 *
 * **Rendered inside the parent dialog's `children`.** Base UI decides nesting from React context, and that is
 * what scopes Esc to the topmost surface. As a sibling, one Esc would close the panel AND the card modal
 * under it. The one part of that default this panel opts out of is the suppressed backdrop — see
 * `showBackdropWhenNested` below.
 *
 * It renders `PersonHolderForm` untouched — the form already owns its schema and submit, and takes `formId`
 * so the action row can live in the drawer's footer.
 */
export const NewHolderDrawer = ({ open, onOpenChange, onResolved, onViewHolder }: NewHolderDrawerProps) => {
  const { t } = useTranslations("cards");
  const [submitting, setSubmitting] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [outcome, setOutcome] = useState<OutcomeState | null>(null);
  const outcomeHeadingRef = useRef<HTMLHeadingElement>(null);
  const outcomeDescriptionId = useId();

  /**
   * Reset on the OPENING edge, not the closing one, and before paint.
   *
   * Closing is the wrong edge for three separate reasons. Base UI keeps the popup mounted through its exit
   * transition, so clearing on close flips the outcome screen back to an empty form for the ~200ms of
   * slide-out — and rebuilds the Zod schema and all 16 RHF fields just to discard them. Worse, the panel is
   * dismissable mid-request: the in-flight read-back then resumes and writes `outcome` on a closed drawer,
   * where a close-edge reset can no longer see it, and the next opening would show the previous creation's
   * result instead of a form.
   *
   * `useLayoutEffect` so a stale outcome cannot paint for a frame on reopen.
   */
  useLayoutEffect(() => {
    if (open) {
      setSubmitting(false);
      setIsResolving(false);
      setOutcome(null);
    }
  }, [open]);

  /**
   * The panel swaps its content without closing, so nothing moves the focus and a screen reader announces
   * nothing — and the submit button the user just pressed is unmounted with the form. Focusing the outcome
   * heading both moves focus somewhere real and reads the result out.
   */
  useEffect(() => {
    if (outcome) outcomeHeadingRef.current?.focus();
  }, [outcome]);

  const handleCreated = useCallback(
    async (response?: CreateIdentityResponseDto) => {
      const createdId = response?.identity?.id;
      setIsResolving(true);

      let readBack: Identity | null = null;
      try {
        if (createdId) {
          const { data } = await IdentitiesService.getIdentityById(createdId, { withAggregates: true });
          readBack = data;
        }
      } catch {
        // Leaves `readBack` null on purpose: the resolver then reports "pending" rather than guessing
        // eligibility from a read it never got.
      } finally {
        setIsResolving(false);
      }

      // The create response is the raw entity — no `kyc_status`, no `kyc_level` — so it can never decide
      // eligibility. Everything below is judged on the read-back. See `CreatedIdentityEntity`.
      const resolved = resolveNewHolderOutcome(createdId, readBack);

      // Reported even if the drawer was dismissed mid-request: the POST already succeeded, so the holder
      // exists. Staying quiet about it is what makes an operator create the same person twice.
      onResolved(resolved);

      if (resolved.kind === "eligible") {
        onOpenChange(false);
        return;
      }
      setOutcome(resolved);
    },
    [onResolved, onOpenChange],
  );

  const isBusy = submitting || isResolving;
  const holderName =
    outcome?.kind === "pending" && outcome.identity ? resolveIdentityDisplayName(outcome.identity) : null;

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={outcome ? t("create_card_holder_created_title") : t("create_card_new_holder_title")}
      description={outcome ? undefined : t("create_card_new_holder_description")}
      size="lg"
      // Nested backdrops are suppressed by default so the page is not veiled twice, but the layer that needs
      // to recede here is the card modal, whose card is opaque and keeps full luminance behind the panel.
      // The scrim falls on it, not on the page, which is what makes the three layers read in order.
      showBackdropWhenNested
      // Required: the body is a single `<ColocaForm>` root, so without this the address section would be
      // sticky-pinned to the bottom edge and float over the fields above it.
      pinLastChild={false}
      footer={
        outcome ? (
          <div className="flex w-full items-center justify-end gap-2">
            {outcome.kind === "pending" && (
              <ColocaButton variant="default" onClick={() => onViewHolder(outcome.identityId)}>
                {t("create_card_holder_created_view")}
              </ColocaButton>
            )}
            <ColocaButton variant="primary" onClick={() => onOpenChange(false)}>
              {t("create_card_holder_created_done")}
            </ColocaButton>
          </div>
        ) : (
          <div className="flex w-full items-center justify-end gap-2">
            <ColocaButton variant="ghost" onClick={() => onOpenChange(false)} disabled={isBusy}>
              {t("create_identity_modal_button_cancel")}
            </ColocaButton>
            <ColocaButton type="submit" variant="primary" form={FORM_ID} loading={isBusy} disabled={isBusy}>
              {t("create_card_new_holder_submit")}
            </ColocaButton>
          </div>
        )
      }
    >
      {outcome ? (
        // `my-auto` centres the block: the drawer body stretches a single root child to full height, which
        // would otherwise strand this short panel at the top of a 100dvh column.
        <div className="my-auto flex flex-col items-center gap-4 px-2 py-8 text-center">
          <span
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-full",
              outcome.kind === "pending"
                ? "bg-warning-container text-warning-container-foreground"
                : "bg-muted text-muted-foreground",
            )}
          >
            {/* Never a success check here: neither branch means "done". */}
            {outcome.kind === "pending" ? (
              <ShieldAlert className="h-6 w-6" aria-hidden="true" />
            ) : (
              <CircleHelp className="h-6 w-6" aria-hidden="true" />
            )}
          </span>
          <div className="space-y-2">
            {/*
              Focus moves here because the button the user pressed is unmounted with the form, and focus
              would otherwise fall to <body>. A focused heading announces only its own name, so the sentence
              that actually explains the outcome rides along via `aria-describedby` — the host's own notice
              cannot do it, since this panel renders the parent dialog `aria-hidden` while it is open.
            */}
            <h3
              ref={outcomeHeadingRef}
              tabIndex={-1}
              aria-describedby={outcomeDescriptionId}
              className="text-base font-semibold text-foreground outline-none"
            >
              {holderName ?? t("create_card_holder_created_title")}
            </h3>
            <p id={outcomeDescriptionId} className="mx-auto max-w-md text-sm text-muted-foreground">
              {outcome.kind === "pending"
                ? t("create_card_holder_created_pending")
                : t("create_card_holder_created_unknown")}
            </p>
          </div>
        </div>
      ) : (
        <PersonHolderForm
          formId={FORM_ID}
          showSuccessToast={false}
          onSuccess={handleCreated}
          onSubmittingChange={setSubmitting}
        />
      )}
    </ColocaDrawer>
  );
};
